import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  List,
  ListItem,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Message as MessageIcon,
} from '@mui/icons-material';
import { getAvatarColor, getInitials } from '../../utils/avatar';
import { formatDate, getRelativeTime } from '../../utils/dateFormat';
import { feedbackService } from '../../services/apiService';

const ComplaintsView = ({ child, childDetails, onFeedbackRead }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const viewedFeedbackIds = useRef(new Set());
  const loadingRef = useRef(false); // Prevent concurrent API calls
  const lastChildIdRef = useRef(null); // Track last loaded child ID

  useEffect(() => {
    const loadFeedbacks = async () => {
      if (!child?.id) {
        setLoading(false);
        lastChildIdRef.current = null;
        return;
      }

      // Prevent duplicate calls for the same child
      if (loadingRef.current) {
        return;
      }

      // Only skip if we already loaded this child's feedback
      if (lastChildIdRef.current === child.id && feedbacks.length > 0) {
        return;
      }

      loadingRef.current = true;
      lastChildIdRef.current = child.id;
      setLoading(true);
      setError(null);
      
      try {
        const response = await feedbackService.getAll({ student_id: child.id });
        const feedbackData = response.data || [];
        setFeedbacks(feedbackData);
        
        // Mark unread feedback as read when loaded
        const unreadFeedbacks = feedbackData.filter(f => !f.is_read || f.is_read === 0);
        const markAsReadPromises = unreadFeedbacks
          .filter(f => !viewedFeedbackIds.current.has(f.id))
          .map(async (feedback) => {
            try {
              await feedbackService.update(feedback.id, { is_read: 1 });
              viewedFeedbackIds.current.add(feedback.id);
              // Update the feedback in state to reflect is_read = 1
              return feedback.id;
            } catch (err) {
              console.error('Error marking feedback as read:', err);
              return null;
            }
          });
        
        const markedIds = await Promise.all(markAsReadPromises);
        // Update feedbacks in state to mark them as read
        if (markedIds.some(id => id !== null)) {
          setFeedbacks(prevFeedbacks => 
            prevFeedbacks.map(f => 
              markedIds.includes(f.id) ? { ...f, is_read: 1 } : f
            )
          );
          // Notify parent component to update unread count (only for this child)
          if (onFeedbackRead) {
            onFeedbackRead();
          }
        }
      } catch (error) {
        console.error('Error loading feedbacks:', error);
        setError('Failed to load feedback. Please try again.');
        setFeedbacks([]);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadFeedbacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child?.id]); // Only depend on child.id, onFeedbackRead is stable

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [feedbacks]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', px: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      maxHeight: '100%',
      overflow: 'hidden',
      width: '100%',
    }}>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 2,
          py: 2,
          height: '100%',
        }}
      >
        {feedbacks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <MessageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" fontWeight={600}>
              No Feedback
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You will see feedback from teachers here.
            </Typography>
          </Box>
        ) : (
          <Box>
            {feedbacks.map((feedback, idx) => {
              const teacherName = feedback.teacher_name || 'Teacher';
              const isSameTeacher = idx > 0 && feedbacks[idx - 1]?.teacher_name === teacherName;
              const feedbackDate = feedback.created_at || feedback.updated_at;
              const prevFeedbackDate = idx > 0 ? (feedbacks[idx - 1]?.created_at || feedbacks[idx - 1]?.updated_at) : null;
              const showDateSeparator = idx === 0 || 
                (feedbackDate && prevFeedbackDate && 
                 new Date(feedbackDate).toDateString() !== new Date(prevFeedbackDate).toDateString());

              return (
                <React.Fragment key={feedback.id || idx}>
                  {showDateSeparator && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ 
                        bgcolor: 'background.paper', 
                        px: 2, 
                        py: 0.5, 
                        borderRadius: 1 
                      }}>
                        {feedbackDate ? formatDate(feedbackDate) : ''}
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      mb: 1.5,
                      px: 1,
                    }}
                  >
                    {!isSameTeacher && (
                      <Avatar
                        sx={{
                          bgcolor: getAvatarColor(teacherName),
                          width: 36,
                          height: 36,
                          fontSize: 14,
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(teacherName)}
                      </Avatar>
                    )}
                    {isSameTeacher && <Box sx={{ width: 36, flexShrink: 0 }} />}
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        maxWidth: '85%',
                      }}
                    >
                      {!isSameTeacher && (
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                          {teacherName}
                        </Typography>
                      )}
                      <Paper
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: 'grey.100',
                          borderTopLeftRadius: isSameTeacher ? 2 : 2,
                          borderTopRightRadius: 2,
                          boxShadow: 'none',
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {feedback.message}
                        </Typography>
                      </Paper>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                        {feedbackDate ? getRelativeTime(feedbackDate) : ''}
                      </Typography>
                    </Box>
                  </Box>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ComplaintsView;

